import client from './client'
import { fetchCsrfCookie } from './auth'
import type {
  RegistrationFormData,
  Registration,
  ParticipantRegisterPayload,
  ParticipantLoginPayload,
  StoreRegistrationPayload,
  UpdateParticipantProfilePayload,
  PaymentIntentResponse,
  ConfirmationData,
} from '@/types/registration'
import type { User } from '@/types/auth'

export async function fetchRegistrationFormData(
  orgSlug: string,
  eventSlug: string,
  raceSlug: string,
): Promise<RegistrationFormData> {
  const { data } = await client.get<{ data: RegistrationFormData }>(
    `/${orgSlug}/${eventSlug}/${raceSlug}/register`,
  )
  return data.data
}

export async function registerParticipant(
  payload: ParticipantRegisterPayload,
): Promise<User> {
  await fetchCsrfCookie()
  const { data } = await client.post<{ data: User }>(
    '/auth/participant/register',
    payload,
  )
  return data.data
}

export async function loginParticipant(
  payload: ParticipantLoginPayload,
): Promise<User> {
  await fetchCsrfCookie()
  const { data } = await client.post<{ data: User }>(
    '/auth/participant/login',
    payload,
  )
  return data.data
}

export async function submitRegistration(
  payload: StoreRegistrationPayload,
): Promise<Registration> {
  await fetchCsrfCookie()
  const { data } = await client.post<{ data: Registration }>(
    '/participant/registrations',
    payload,
  )
  return data.data
}

export async function uploadEventLogo(
  eventSlug: string,
  file: File,
): Promise<{ logo_url: string | null }> {
  const formData = new FormData()
  formData.append('logo', file)
  const { data } = await client.post<{ data: { logo_url: string | null } }>(
    `/organizer/events/${eventSlug}/logo`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return data.data
}

export async function updateParticipantProfile(
  payload: UpdateParticipantProfilePayload,
): Promise<User> {
  const { data } = await client.put<{ data: User }>(
    '/participant/profile',
    payload,
  )
  return data.data
}

export async function logoutParticipant(): Promise<void> {
  await client.post('/auth/logout')
}

export async function deleteEventLogo(
  eventSlug: string,
): Promise<void> {
  await client.delete(`/organizer/events/${eventSlug}/logo`)
}

export async function fetchMyRaceRegistration(raceId: number): Promise<Registration | null> {
  try {
    const { data } = await client.get<{ data: Registration }>(
      `/participant/registrations/pending/${raceId}`,
    )
    return data.data
  } catch {
    return null
  }
}

export async function createPaymentIntent(
  registrationId: number,
): Promise<PaymentIntentResponse> {
  const { data } = await client.post<{ data: PaymentIntentResponse }>(
    `/participant/registrations/${registrationId}/create-payment-intent`,
  )
  return data.data
}

export async function fetchConfirmation(
  confirmationToken: string,
): Promise<ConfirmationData> {
  const { data } = await client.get<{ data: ConfirmationData }>(
    `/registrations/confirm/${confirmationToken}`,
  )
  return data.data
}
